/**
 * BuildingState — full-screen game state for placing and managing buildings
 * on the labyrinth map. Shows the base area with a side panel for the
 * building catalog.
 */
import type { Texture, FederatedPointerEvent } from 'pixi.js';
import { Assets, Container, Graphics, Sprite, Text } from 'pixi.js';
import { draw3DButton, drawBevelPanel, drawHeaderBand, darken, lighten, GUGI_SHADOW } from '../rendering/PanelUtils';
import type { GameState, GameStateManager } from './GameStateManager';
import { BaseState } from './BaseState';
import { Labyrinth, TILE_SIZE, Tile } from '../labyrinth/Labyrinth';
import { loadMapFromSVG } from '../labyrinth/MapLoader';
import { ALL_BUILDINGS, getBuildingById, type BuildingId, type BuildingDef } from '../buildings/BuildingData';
import { drawGhostFootprint, rebuildBuildingSprites, rebuildBuildingLabels, loadBuildingTextures, type BuildingTextureMap } from '../rendering/BuildingRenderer';
import { getViewport, type ViewportInfo } from '../rendering/Viewport';

// ─── Accent palette ──────────────────────────────────────
const ACCENT      = 0xDD2233;
const ACCENT_DIM  = 0x881122;
const ACCENT_DARK = 0x2a1015;

// ─── Layout ──────────────────────────────────────────────
const PANEL_W_DESKTOP = 360;  // desktop right panel width
const PANEL_W_MIN = 220;      // mobile minimum — below this the catalog is unusable
const PAD = 10;

/** Viewport-aware panel width so narrow screens keep the map area usable. */
function computePanelW(screenW: number): number {
  return Math.max(PANEL_W_MIN, Math.min(PANEL_W_DESKTOP, Math.round(screenW * 0.4)));
}

// ─── Map rendering ──────────────────────────────────────
const BASE_PAD = 8;      // extra tiles around base area
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3.0;
const ZOOM_SPEED = 0.15;

// LOD flat colors (match LabyrinthState)
const LOD_FLAT_WALL  = 0x3a1018;
const LOD_FLAT_FLOOR = 0x282c34;
const LOD_FLAT_BASE  = 0x1a3a30;

export class BuildingState implements GameState {
  private container = new Container();
  private mapContainer = new Container();
  private uiContainer = new Container();
  private manager: GameStateManager;
  private labyrinth!: Labyrinth;

  // Map rendering
  private tileContainer!: Container;
  private tileGraphics!: Graphics;
  private buildingSpriteContainer!: Container;
  private buildingOverlayGraphics!: Graphics;
  private buildingLabelContainer!: Container;
  private wallTextures: Texture[] = [];
  private floorTexture: Texture | null = null;
  private buildingTextures: BuildingTextureMap = new Map();
  private wallSprites = new Map<string, Sprite>();
  private floorSprites = new Map<string, Sprite>();
  private wallVariantMap = new Map<string, number>();

  // Camera
  private zoom = 1.5;
  private camX = 0; // world tile X center
  private camY = 0;
  private dragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragCamStartX = 0;
  private dragCamStartY = 0;

  // Visible tile bounds
  private viewMinX = 0;
  private viewMinY = 0;
  private viewMaxX = 0;
  private viewMaxY = 0;

  // Placement/selection state
  private placingBuilding: BuildingDef | null = null;
  private placingMode: 'build' | 'move' = 'build';
  private movingIndex = -1;
  private selectedBuildingIndex = -1;
  private ghostGx = -1;
  private ghostGy = -1;
  private ghostValid = false;

  // UI
  private panelRoot: Container = new Container();
  private mapClip: Graphics | null = null;
  private mapHitArea: Graphics | null = null;
  private catalogContainer!: Container;
  private catalogContent!: Container;
  private catalogMask!: Graphics;
  private catalogTotalH = 0;
  private creditsText!: Text;
  private statusText!: Text;

  // Input handlers
  private wheelHandler: ((e: WheelEvent) => void) | null = null;
  private contextMenuHandler: ((e: Event) => void) | null = null;

  private loading = true;
  private mapAreaW = 0; // pixels available for map (screen width - panel)
  private mapAreaH = 0; // pixels available for map vertically (screen height - drawer on mobile)
  private panelW = PANEL_W_DESKTOP;
  private panelH = 0;   // panel height in px
  private panelX = 0;
  private panelY = 0;
  private vp!: ViewportInfo;
  // Mobile bottom drawer: persists across rebuilds via a static so toggling
  // feels sticky if the state manager ever recreates BuildingState.
  private static drawerCollapsed = false;
  private static readonly DRAWER_COLLAPSED_H = 40;

  constructor(manager: GameStateManager) {
    this.manager = manager;
  }

  enter() {
    const app = this.manager.app;
    app.stage.addChild(this.container);
    this.container.addChild(this.mapContainer);
    this.container.addChild(this.uiContainer);

    this.vp = getViewport(app);
    if (this.vp.mode === 'mobile') {
      // Bottom drawer: full width, ~45% of screen height (collapsed peeks 40px)
      this.panelW = app.screen.width;
      this.panelH = BuildingState.drawerCollapsed
        ? BuildingState.DRAWER_COLLAPSED_H
        : Math.round(app.screen.height * 0.45);
      this.panelX = 0;
      this.panelY = app.screen.height - this.panelH;
      this.mapAreaW = app.screen.width;
      this.mapAreaH = app.screen.height - this.panelH;
    } else {
      this.panelW = computePanelW(app.screen.width);
      this.panelH = app.screen.height;
      this.panelX = app.screen.width - this.panelW;
      this.panelY = 0;
      this.mapAreaW = app.screen.width - this.panelW;
      this.mapAreaH = app.screen.height;
    }

    void this.loadAssets().then(() => {
      // Camera + zoom depend on base bounds, which are only valid after
      // the labyrinth has been loaded and initBaseBounds() has run.
      const bs = this.manager.buildings;
      this.camX = bs.baseOriginX + bs.baseW / 2;
      this.camY = bs.baseOriginY + bs.baseH / 2;
      const fitZoomX = this.mapAreaW / ((bs.baseW + BASE_PAD * 2) * TILE_SIZE);
      const fitZoomY = this.mapAreaH / ((bs.baseH + BASE_PAD * 2) * TILE_SIZE);
      this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, Math.min(fitZoomX, fitZoomY)));

      this.loading = false;
      this.setupScene();
    }).catch((err) => {
      console.error('[BuildingState] failed to enter:', err);
    });
  }

  /** Load the labyrinth (if not already loaded) plus tile/building textures.
   *  On a fresh reset the map is lazily loaded only by LabyrinthState or
   *  BaseState's minimap, so BuildingState must self-bootstrap to avoid
   *  rendering against a null labyrinth. */
  private async loadAssets() {
    if (!this.manager.labyrinth) {
      const mapData = await loadMapFromSVG('/maps/placeholder-map.svg');
      const labyrinth = new Labyrinth(
        mapData.width, mapData.height, mapData.tiles, mapData.baseTile, mapData.doors,
      );
      this.manager.initExploration(labyrinth);
    }
    this.labyrinth = this.manager.labyrinth!;
    await this.loadTextures();
  }

  private async loadTextures() {
    const [w1, w2, w3, w4, w5, floor, bldgTextures] = await Promise.all([
      Assets.load<Texture>('/maps/walls.svg'),
      Assets.load<Texture>('/maps/walls2.svg'),
      Assets.load<Texture>('/maps/walls3.svg'),
      Assets.load<Texture>('/maps/walls4.svg'),
      Assets.load<Texture>('/maps/walls5.svg'),
      Assets.load<Texture>('/maps/floor.svg'),
      loadBuildingTextures(),
    ]);
    this.wallTextures = [w1, w2, w3, w4, w5];
    this.floorTexture = floor;
    this.buildingTextures = bldgTextures;
  }

  private getWallVariant(x: number, y: number): number {
    const key = `${x},${y}`;
    let v = this.wallVariantMap.get(key);
    if (v === undefined) {
      v = ((x * 73856093) ^ (y * 19349663)) >>> 0;
      v = v % this.wallTextures.length;
      this.wallVariantMap.set(key, v);
    }
    return v;
  }

  private getWallRotation(x: number, y: number): number {
    const lab = this.labyrinth;
    const n = lab.isWalkable(x, y - 1);
    const s = lab.isWalkable(x, y + 1);
    const e = lab.isWalkable(x + 1, y);
    const w = lab.isWalkable(x - 1, y);

    if (s && !n) return 0;
    if (n && !s) return Math.PI;
    if (e && !w) return -Math.PI / 2;
    if (w && !e) return Math.PI / 2;
    if (n && s) return 0;
    if (e && w) return Math.PI / 2;

    const hash = (((x * 73856093) ^ (y * 19349663)) >>> 0) % 4;
    return hash * (Math.PI / 2);
  }

  private setupScene() {
    const app = this.manager.app;
    const bs = this.manager.buildings;

    // ── Map layers ──────────────────────────────────────
    this.tileContainer = new Container();
    this.mapContainer.addChild(this.tileContainer);

    this.tileGraphics = new Graphics();
    this.tileContainer.addChild(this.tileGraphics);

    this.buildingSpriteContainer = new Container();
    this.mapContainer.addChild(this.buildingSpriteContainer);

    this.buildingOverlayGraphics = new Graphics();
    this.mapContainer.addChild(this.buildingOverlayGraphics);

    this.buildingLabelContainer = new Container();
    this.mapContainer.addChild(this.buildingLabelContainer);

    // Clip map to the available area (top portion on mobile, left portion on desktop)
    const mapClip = new Graphics();
    mapClip.rect(0, 0, this.mapAreaW, this.mapAreaH);
    mapClip.fill(0xffffff);
    this.mapContainer.mask = mapClip;
    this.container.addChild(mapClip);
    this.mapClip = mapClip;

    // Initial tile render
    this.computeViewBounds();
    this.drawTiles();
    this.drawBuildings();

    // ── Right panel ─────────────────────────────────────
    this.buildPanel();

    // ── Input ───────────────────────────────────────────
    // Map click area for building interaction
    const mapHitArea = new Graphics();
    mapHitArea.rect(0, 0, this.mapAreaW, this.mapAreaH);
    mapHitArea.fill({ color: 0xffffff, alpha: 0.001 });
    mapHitArea.eventMode = 'static';
    mapHitArea.cursor = 'grab';
    this.uiContainer.addChildAt(mapHitArea, 0);
    this.mapHitArea = mapHitArea;

    mapHitArea.on('pointerdown', (e: FederatedPointerEvent) => {
      if (e.button === 2) {
        // Right-click: cancel placement
        if (this.placingBuilding) {
          this.exitPlacementMode();
          this.rebuildAll();
        }
        return;
      }

      const worldPos = this.screenToWorld(e.globalX, e.globalY);

      if (this.placingBuilding && this.ghostValid && this.ghostGx >= 0) {
        // Execute placement
        if (this.placingMode === 'build') {
          const cost = this.placingBuilding.costs[0]!;
          if (this.manager.money >= cost) {
            const success = bs.place(this.placingBuilding.id, this.ghostGx, this.ghostGy);
            if (success) {
              this.manager.money -= cost;
              this.manager.save();
            }
          }
        } else if (this.placingMode === 'move' && this.movingIndex >= 0) {
          bs.move(this.movingIndex, this.ghostGx, this.ghostGy);
          this.manager.save();
        }
        this.exitPlacementMode();
        this.rebuildAll();
        return;
      }

      if (this.placingBuilding) return; // In placement mode, ignore

      // Check if clicking on a placed building
      const gx = Math.floor(worldPos.x) - bs.baseOriginX;
      const gy = Math.floor(worldPos.y) - bs.baseOriginY;

      let foundIndex = -1;
      for (let i = 0; i < bs.buildings.length; i++) {
        const b = bs.buildings[i]!;
        const def = getBuildingById(b.id);
        if (!def) continue;
        if (gx >= b.gridX && gx < b.gridX + def.footprintW &&
            gy >= b.gridY && gy < b.gridY + def.footprintH) {
          foundIndex = i;
          break;
        }
      }

      if (foundIndex >= 0) {
        this.selectedBuildingIndex = foundIndex;
        this.rebuildAll();
        return;
      }

      // Deselect if clicking empty area
      if (this.selectedBuildingIndex >= 0) {
        this.selectedBuildingIndex = -1;
        this.rebuildAll();
        return;
      }

      // Start drag
      this.dragging = true;
      this.dragStartX = e.globalX;
      this.dragStartY = e.globalY;
      this.dragCamStartX = this.camX;
      this.dragCamStartY = this.camY;
      mapHitArea.cursor = 'grabbing';
    });

    mapHitArea.on('pointermove', (e: FederatedPointerEvent) => {
      if (this.dragging) {
        const dx = (e.globalX - this.dragStartX) / (TILE_SIZE * this.zoom);
        const dy = (e.globalY - this.dragStartY) / (TILE_SIZE * this.zoom);
        this.camX = this.dragCamStartX - dx;
        this.camY = this.dragCamStartY - dy;
        this.clampCamera();
        return;
      }

      if (this.placingBuilding) {
        const worldPos = this.screenToWorld(e.globalX, e.globalY);
        const gx = Math.floor(worldPos.x) - bs.baseOriginX;
        const gy = Math.floor(worldPos.y) - bs.baseOriginY;

        const cx = gx - Math.floor(this.placingBuilding.footprintW / 2);
        const cy = gy - Math.floor(this.placingBuilding.footprintH / 2);

        this.ghostGx = cx;
        this.ghostGy = cy;

        if (this.placingMode === 'move') {
          this.ghostValid = bs.canPlace(this.placingBuilding.id, cx, cy, this.movingIndex);
        } else {
          this.ghostValid = bs.canPlace(this.placingBuilding.id, cx, cy);
        }

        mapHitArea.cursor = this.ghostValid ? 'pointer' : 'not-allowed';
      }
    });

    mapHitArea.on('pointerup', () => {
      if (this.dragging) {
        this.dragging = false;
        mapHitArea.cursor = this.placingBuilding ? 'crosshair' : 'grab';
      }
    });

    mapHitArea.on('pointerleave', () => {
      if (this.dragging) {
        this.dragging = false;
        mapHitArea.cursor = 'grab';
      }
      if (this.placingBuilding) {
        this.ghostGx = -1;
        this.ghostGy = -1;
      }
    });

    // Zoom
    this.wheelHandler = (e: WheelEvent) => {
      // Only handle wheel events on the map area
      if (e.clientX > this.mapAreaW || e.clientY > this.mapAreaH) return;
      e.preventDefault();
      const dir = e.deltaY < 0 ? 1 : -1;
      this.zoom *= 1 + dir * ZOOM_SPEED;
      this.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.zoom));
    };
    app.canvas.addEventListener('wheel', this.wheelHandler, { passive: false });

    this.contextMenuHandler = (e: Event) => e.preventDefault();
    app.canvas.addEventListener('contextmenu', this.contextMenuHandler);
  }

  private screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    const camOffX = this.mapAreaW / 2 - this.camX * TILE_SIZE * this.zoom;
    const camOffY = this.mapAreaH / 2 - this.camY * TILE_SIZE * this.zoom;
    return {
      x: (screenX - camOffX) / (TILE_SIZE * this.zoom),
      y: (screenY - camOffY) / (TILE_SIZE * this.zoom),
    };
  }

  private clampCamera() {
    const bs = this.manager.buildings;
    const minX = bs.baseOriginX - BASE_PAD;
    const maxX = bs.baseOriginX + bs.baseW + BASE_PAD;
    const minY = bs.baseOriginY - BASE_PAD;
    const maxY = bs.baseOriginY + bs.baseH + BASE_PAD;
    this.camX = Math.max(minX, Math.min(maxX, this.camX));
    this.camY = Math.max(minY, Math.min(maxY, this.camY));
  }

  private computeViewBounds() {
    const bs = this.manager.buildings;
    this.viewMinX = Math.max(0, bs.baseOriginX - BASE_PAD);
    this.viewMinY = Math.max(0, bs.baseOriginY - BASE_PAD);
    this.viewMaxX = Math.min(this.labyrinth.width, bs.baseOriginX + bs.baseW + BASE_PAD);
    this.viewMaxY = Math.min(this.labyrinth.height, bs.baseOriginY + bs.baseH + BASE_PAD);
  }

  private drawTiles() {
    const g = this.tileGraphics;
    g.clear();

    // Clear existing sprites
    for (const [, sprite] of this.wallSprites) {
      this.tileContainer.removeChild(sprite);
      sprite.destroy();
    }
    this.wallSprites.clear();
    for (const [, sprite] of this.floorSprites) {
      this.tileContainer.removeChild(sprite);
      sprite.destroy();
    }
    this.floorSprites.clear();

    const useSprites = this.wallTextures.length > 0;

    for (let y = this.viewMinY; y < this.viewMaxY; y++) {
      for (let x = this.viewMinX; x < this.viewMaxX; x++) {
        const tile = this.labyrinth.tiles[y]?.[x];
        if (tile === undefined) continue;

        if (!useSprites) {
          const color = tile === Tile.Wall ? LOD_FLAT_WALL
            : (tile === Tile.Base || tile === Tile.Door) ? LOD_FLAT_BASE
            : LOD_FLAT_FLOOR;
          g.rect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
          g.fill(color);
          continue;
        }

        const key = `${x},${y}`;

        if (tile === Tile.Wall) {
          const variant = this.getWallVariant(x, y);
          const sprite = new Sprite(this.wallTextures[variant]);
          sprite.anchor.set(0.5, 0.5);
          sprite.x = x * TILE_SIZE + TILE_SIZE / 2;
          sprite.y = y * TILE_SIZE + TILE_SIZE / 2;
          sprite.width = TILE_SIZE;
          sprite.height = TILE_SIZE;
          sprite.rotation = this.getWallRotation(x, y);
          this.tileContainer.addChild(sprite);
          this.wallSprites.set(key, sprite);
        } else if (tile === Tile.Floor || tile === Tile.Base || tile === Tile.Door) {
          if (this.floorTexture) {
            const sprite = new Sprite(this.floorTexture);
            sprite.x = x * TILE_SIZE;
            sprite.y = y * TILE_SIZE;
            sprite.width = TILE_SIZE;
            sprite.height = TILE_SIZE;
            if (tile === Tile.Base) sprite.tint = 0x33aa88;
            else if (tile === Tile.Door) sprite.tint = 0x88aa33;
            this.tileContainer.addChild(sprite);
            this.floorSprites.set(key, sprite);
          } else {
            g.rect(x * TILE_SIZE, y * TILE_SIZE, TILE_SIZE, TILE_SIZE);
            g.fill((tile === Tile.Base || tile === Tile.Door) ? LOD_FLAT_BASE : LOD_FLAT_FLOOR);
          }
        }
      }
    }
  }

  private drawBuildings() {
    const bs = this.manager.buildings;
    rebuildBuildingSprites(this.buildingSpriteContainer, bs.buildings, bs.baseOriginX, bs.baseOriginY, this.buildingTextures);

    // Overlay: ghost footprint + selection highlight
    this.buildingOverlayGraphics.clear();

    // Draw ghost footprint
    if (this.placingBuilding && this.ghostGx >= 0 && this.ghostGy >= 0) {
      drawGhostFootprint(
        this.buildingOverlayGraphics,
        bs.baseOriginX + this.ghostGx,
        bs.baseOriginY + this.ghostGy,
        this.placingBuilding.footprintW,
        this.placingBuilding.footprintH,
        this.ghostValid,
      );
    }

    // Selection highlight
    if (this.selectedBuildingIndex >= 0 && this.selectedBuildingIndex < bs.buildings.length) {
      const b = bs.buildings[this.selectedBuildingIndex]!;
      const def = getBuildingById(b.id);
      if (def) {
        const wx = (bs.baseOriginX + b.gridX) * TILE_SIZE;
        const wy = (bs.baseOriginY + b.gridY) * TILE_SIZE;
        const bw = def.footprintW * TILE_SIZE;
        const bh = def.footprintH * TILE_SIZE;
        this.buildingOverlayGraphics.rect(wx + 2, wy + 2, bw - 4, bh - 4);
        this.buildingOverlayGraphics.stroke({ color: 0xffffff, width: 2, alpha: 0.9 });
      }
    }

    rebuildBuildingLabels(this.buildingLabelContainer, bs.buildings, bs.baseOriginX, bs.baseOriginY);
  }

  // ─── Right side panel ─────────────────────────────────

  private buildPanel() {
    const panelX = this.panelX;
    const panelY = this.panelY;
    const panelW = this.panelW;
    const panelH = this.panelH;

    // Clear and re-host under panelRoot so we can destroy/rebuild on drawer toggle
    if (this.panelRoot.parent) this.panelRoot.parent.removeChild(this.panelRoot);
    for (const c of this.panelRoot.removeChildren()) c.destroy({ children: true });
    this.uiContainer.addChild(this.panelRoot);
    const host = this.panelRoot;

    // Panel background
    const panelBg = new Graphics();
    drawBevelPanel(panelBg, panelX, panelY, panelW, panelH, { fill: 0x0c0e14, border: ACCENT_DIM, radius: 0 });
    host.addChild(panelBg);

    // Mobile: collapse/expand chevron handle sits above the panel top edge
    if (this.vp.mode === 'mobile') {
      const handle = new Container();
      const HANDLE_W = 68, HANDLE_H = 28;
      handle.position.set(panelX + (panelW - HANDLE_W) / 2, panelY - HANDLE_H + 2);
      const hbg = new Graphics();
      hbg.roundRect(0, 0, HANDLE_W, HANDLE_H, 8);
      hbg.fill({ color: 0x0c0e14, alpha: 0.95 });
      hbg.stroke({ color: ACCENT_DIM, width: 1 });
      handle.addChild(hbg);
      const chevron = new Graphics();
      const ccx = HANDLE_W / 2;
      const ccy = HANDLE_H / 2 + 1;
      if (BuildingState.drawerCollapsed) {
        chevron.moveTo(ccx - 8, ccy + 3).lineTo(ccx, ccy - 4).lineTo(ccx + 8, ccy + 3);
      } else {
        chevron.moveTo(ccx - 8, ccy - 3).lineTo(ccx, ccy + 4).lineTo(ccx + 8, ccy - 3);
      }
      chevron.stroke({ color: ACCENT, width: 2 });
      handle.addChild(chevron);
      handle.eventMode = 'static';
      handle.cursor = 'pointer';
      handle.hitArea = { contains: (hx, hy) =>
        hx >= -8 && hx <= HANDLE_W + 8 && hy >= -8 && hy <= HANDLE_H + 8 };
      handle.on('pointerdown', (e: FederatedPointerEvent) => {
        e.stopPropagation();
        BuildingState.drawerCollapsed = !BuildingState.drawerCollapsed;
        this.toggleDrawer();
      });
      host.addChild(handle);

      // When collapsed, just render a one-line "BUILDINGS" title strip
      if (BuildingState.drawerCollapsed) {
        const collapsedTitle = new Text({
          text: 'BUILDINGS',
          style: { fontFamily: 'Gugi', fontSize: 14, fill: ACCENT, dropShadow: GUGI_SHADOW },
        });
        collapsedTitle.anchor.set(0.5, 0.5);
        collapsedTitle.position.set(panelX + panelW / 2, panelY + panelH / 2);
        host.addChild(collapsedTitle);
        return;
      }
    }

    // Header
    const headerBand = new Graphics();
    drawHeaderBand(headerBand, panelX, panelY + 4, panelW, ACCENT_DARK, 0x0c0e14, 30);
    host.addChild(headerBand);

    const title = new Text({ text: 'BUILDINGS', style: { fontFamily: 'Gugi', fontSize: 20, fill: ACCENT, dropShadow: GUGI_SHADOW } });
    title.position.set(panelX + PAD, panelY + 7);
    host.addChild(title);

    // Credits
    this.creditsText = new Text({
      text: `Cr  ${this.manager.money.toLocaleString()}`,
      style: { fontFamily: 'Offside', fontSize: 14, fill: 0xffdd44 },
    });
    this.creditsText.anchor.set(1, 0);
    this.creditsText.position.set(panelX + panelW - PAD, panelY + 10);
    host.addChild(this.creditsText);

    // Status text
    this.statusText = new Text({
      text: '',
      style: { fontFamily: 'Offside', fontSize: 11, fill: 0x88aacc },
    });
    this.statusText.position.set(panelX + PAD, panelY + 30);
    host.addChild(this.statusText);

    // BACK button
    const backBtn = new Container();
    const backBg = new Graphics();
    const backBtnW = panelW - PAD * 2;
    const backBtnH = 34;
    draw3DButton(backBg, backBtnW, backBtnH, 0x333344);
    backBtn.addChild(backBg);
    const backLabel = new Text({ text: 'BACK', style: { fontFamily: 'Gugi', fontSize: 16, fill: 0xffffff, dropShadow: GUGI_SHADOW } });
    backLabel.anchor.set(0.5);
    backLabel.position.set(backBtnW / 2, backBtnH / 2);
    backBtn.addChild(backLabel);
    backBtn.position.set(panelX + PAD, panelY + panelH - backBtnH - PAD);
    backBtn.eventMode = 'static';
    backBtn.cursor = 'pointer';
    backBtn.on('pointerover', () => { backBg.tint = 0xcccccc; });
    backBtn.on('pointerout', () => { backBg.tint = 0xffffff; });
    backBtn.on('pointerdown', () => {
      this.manager.save();
      this.manager.switchTo(new BaseState(this.manager));
    });
    host.addChild(backBtn);

    // Catalog area
    const catalogY = panelY + 48;
    const catalogH = panelH - 48 - backBtnH - PAD * 2 - 8;

    this.catalogContainer = new Container();
    this.catalogContainer.position.set(panelX + PAD, catalogY);
    host.addChild(this.catalogContainer);

    this.catalogContent = new Container();
    this.catalogContainer.addChild(this.catalogContent);

    this.catalogMask = new Graphics();
    this.catalogMask.rect(0, 0, panelW - PAD * 2, catalogH);
    this.catalogMask.fill(0xffffff);
    this.catalogContainer.addChild(this.catalogMask);
    this.catalogContent.mask = this.catalogMask;

    // Scroll (wheel + pointer drag for touch)
    this.catalogContainer.eventMode = 'static';
    this.catalogContainer.on('wheel', (e: WheelEvent) => {
      const maxScroll = Math.max(0, this.catalogTotalH - catalogH);
      this.catalogContent.y = Math.max(-maxScroll, Math.min(0, this.catalogContent.y - e.deltaY * 0.5));
    });
    let dragY = 0, draggingCatalog = false, dragStartContentY = 0;
    this.catalogContainer.on('pointerdown', (e: FederatedPointerEvent) => {
      draggingCatalog = true;
      dragY = e.global.y;
      dragStartContentY = this.catalogContent.y;
    });
    this.catalogContainer.on('globalpointermove', (e: FederatedPointerEvent) => {
      if (!draggingCatalog) return;
      const maxScroll = Math.max(0, this.catalogTotalH - catalogH);
      const delta = e.global.y - dragY;
      this.catalogContent.y = Math.max(-maxScroll, Math.min(0, dragStartContentY + delta));
    });
    const stopDrag = () => { draggingCatalog = false; };
    this.catalogContainer.on('pointerup', stopDrag);
    this.catalogContainer.on('pointerupoutside', stopDrag);
    this.catalogContainer.on('pointercancel', stopDrag);

    this.buildCatalog();
  }

  private buildCatalog() {
    this.catalogContent.removeChildren();
    this.catalogTotalH = 0;

    const bs = this.manager.buildings;
    const w = this.panelW - PAD * 2 - 4;
    let yOff = 0;

    // Selected building detail panel
    if (this.selectedBuildingIndex >= 0 && this.selectedBuildingIndex < bs.buildings.length) {
      const b = bs.buildings[this.selectedBuildingIndex]!;
      const def = getBuildingById(b.id);
      if (def) {
        yOff = this.buildSelectedPanel(b, def, this.selectedBuildingIndex, w, yOff);
        yOff += 8;

        const sep = new Graphics();
        sep.moveTo(4, yOff);
        sep.lineTo(w - 4, yOff);
        sep.stroke({ color: ACCENT_DIM, width: 1, alpha: 0.5 });
        this.catalogContent.addChild(sep);
        yOff += 8;
      }
    }

    // List all buildings
    for (const def of ALL_BUILDINGS) {
      yOff = this.buildCatalogEntry(def, w, yOff);
      yOff += 6;
    }

    this.catalogTotalH = yOff;
  }

  private buildSelectedPanel(
    b: { id: BuildingId; level: number; gridX: number; gridY: number },
    def: BuildingDef, bIndex: number, _w: number, startY: number,
  ): number {
    const bs = this.manager.buildings;
    let y = startY;

    // Header
    const header = new Text({
      text: `${def.icon} ${def.name}  Lv ${b.level}`,
      style: { fontFamily: 'Gugi', fontSize: 15, fill: def.color, dropShadow: GUGI_SHADOW },
    });
    header.position.set(4, y);
    this.catalogContent.addChild(header);
    y += 24;

    // Current effect
    if (b.level >= 1 && b.level <= def.effectDescriptions.length) {
      const effectText = new Text({
        text: def.effectDescriptions[b.level - 1]!,
        style: { fontFamily: 'Offside', fontSize: 12, fill: 0x88ccaa },
      });
      effectText.position.set(8, y);
      this.catalogContent.addChild(effectText);
      y += 20;
    }

    // Button row
    const btnY = y + 4;
    const btnH = 28;

    // UPGRADE button
    if (b.level < def.maxLevel) {
      const upgCost = def.costs[b.level]!;
      const canAfford = this.manager.money >= upgCost;

      const upgBtn = new Container();
      const upgBg = new Graphics();
      const ubw = 110;
      draw3DButton(upgBg, ubw, btnH, canAfford ? 0x226633 : 0x333333, { radius: 4, depth: 2 });
      upgBtn.addChild(upgBg);
      const upgLabel = new Text({
        text: `UPGRADE ${upgCost}Cr`,
        style: { fontFamily: 'Offside', fontSize: 12, fill: canAfford ? 0xffffff : 0x99aabb },
      });
      upgLabel.anchor.set(0.5);
      upgLabel.position.set(ubw / 2, btnH / 2);
      upgBtn.addChild(upgLabel);
      upgBtn.position.set(4, btnY);
      upgBtn.eventMode = 'static';
      upgBtn.cursor = canAfford ? 'pointer' : 'not-allowed';
      if (canAfford) {
        upgBtn.on('pointerover', () => { upgBg.tint = 0xcccccc; });
        upgBtn.on('pointerout', () => { upgBg.tint = 0xffffff; });
        upgBtn.on('pointerdown', () => {
          const cost = bs.upgrade(bIndex, this.manager.money);
          if (cost > 0) {
            this.manager.money -= cost;
            this.manager.save();
            this.rebuildAll();
          }
        });
      }
      this.catalogContent.addChild(upgBtn);
    } else {
      const maxLabel = new Text({
        text: 'MAX LEVEL',
        style: { fontFamily: 'Offside', fontSize: 12, fill: 0x667788 },
      });
      maxLabel.position.set(8, btnY + 6);
      this.catalogContent.addChild(maxLabel);
    }

    // MOVE button
    const moveBtn = new Container();
    const moveBg = new Graphics();
    const moveBtnW = 70;
    draw3DButton(moveBg, moveBtnW, btnH, 0x334466, { radius: 4, depth: 2 });
    moveBtn.addChild(moveBg);
    const moveLabel = new Text({
      text: 'MOVE',
      style: { fontFamily: 'Offside', fontSize: 12, fill: 0xffffff },
    });
    moveLabel.anchor.set(0.5);
    moveLabel.position.set(moveBtnW / 2, btnH / 2);
    moveBtn.addChild(moveLabel);
    moveBtn.position.set(120, btnY);
    moveBtn.eventMode = 'static';
    moveBtn.cursor = 'pointer';
    moveBtn.on('pointerover', () => { moveBg.tint = 0xcccccc; });
    moveBtn.on('pointerout', () => { moveBg.tint = 0xffffff; });
    moveBtn.on('pointerdown', () => {
      this.placingBuilding = def;
      this.placingMode = 'move';
      this.movingIndex = bIndex;
      this.selectedBuildingIndex = -1;
      this.statusText.text = `Moving ${def.name}`;
      this.rebuildAll();
    });
    this.catalogContent.addChild(moveBtn);

    // DEMOLISH button
    const demBtn = new Container();
    const demBg = new Graphics();
    const demBtnW = 90;
    draw3DButton(demBg, demBtnW, btnH, 0x662222, { radius: 4, depth: 2 });
    demBtn.addChild(demBg);
    const demLabel = new Text({
      text: 'DEMOLISH',
      style: { fontFamily: 'Offside', fontSize: 12, fill: 0xff6666 },
    });
    demLabel.anchor.set(0.5);
    demLabel.position.set(demBtnW / 2, btnH / 2);
    demBtn.addChild(demLabel);
    demBtn.position.set(196, btnY);
    demBtn.eventMode = 'static';
    demBtn.cursor = 'pointer';
    demBtn.on('pointerover', () => { demBg.tint = 0xcccccc; });
    demBtn.on('pointerout', () => { demBg.tint = 0xffffff; });
    demBtn.on('pointerdown', () => {
      bs.remove(bIndex);
      this.selectedBuildingIndex = -1;
      this.manager.save();
      this.rebuildAll();
    });
    this.catalogContent.addChild(demBtn);

    y = btnY + btnH + 8;
    return y;
  }

  private buildCatalogEntry(def: BuildingDef, w: number, startY: number): number {
    const bs = this.manager.buildings;
    const builtLevel = bs.getBuildingLevel(def.id);
    const isBuilt = builtLevel > 0;
    const iconBarW = 34;
    const textX = iconBarW + 10;
    const btnH = 26;

    // --- Measure description height dynamically ---
    const descText = new Text({
      text: def.description,
      style: { fontFamily: 'Offside', fontSize: 11, fill: 0x889aab, wordWrap: true, wordWrapWidth: w - textX - 8 },
    });

    // Layout: name(20) + size(16) + desc(dynamic) + gap(6) + max(effect, button) row + pad(8)
    const descH = descText.height;
    const bottomRowH = isBuilt ? Math.max(18, btnH) + 4 : btnH + 4;
    const contentH = 4 + 20 + 16 + descH + 6 + bottomRowH + 6;
    const entryH = Math.max(contentH, 70); // minimum height

    let cy: number; // cursor within entry

    // Entry background
    const entryBg = new Graphics();
    entryBg.roundRect(0, startY, w, entryH, 4);
    entryBg.fill({ color: 0x0e1018, alpha: 0.7 });
    entryBg.roundRect(0, startY, w, entryH, 4);
    entryBg.stroke({ color: isBuilt ? darken(def.color, 0.1) : 0x1a2030, width: 1, alpha: isBuilt ? 0.5 : 0.3 });
    this.catalogContent.addChild(entryBg);

    // Color icon bar
    const iconBar = new Graphics();
    iconBar.roundRect(2, startY + 2, iconBarW, entryH - 4, 3);
    iconBar.fill({ color: darken(def.color, 0.2), alpha: 0.8 });
    this.catalogContent.addChild(iconBar);

    const iconText = new Text({
      text: def.icon,
      style: { fontSize: 18, fill: 0xffffff },
    });
    iconText.anchor.set(0.5);
    iconText.position.set(2 + iconBarW / 2, startY + entryH / 2);
    this.catalogContent.addChild(iconText);

    // Name
    cy = startY + 4;
    const nameText = new Text({
      text: def.name,
      style: { fontFamily: 'Offside', fontSize: 14, fill: isBuilt ? lighten(def.color, 0.15) : 0xccccdd },
    });
    nameText.position.set(textX, cy);
    this.catalogContent.addChild(nameText);

    if (isBuilt) {
      // Level indicator (top-right)
      const lvText = new Text({
        text: `Lv ${builtLevel}/${def.maxLevel}`,
        style: { fontFamily: 'Offside', fontSize: 12, fill: 0xaabbcc },
      });
      lvText.anchor.set(1, 0);
      lvText.position.set(w - 6, cy);
      this.catalogContent.addChild(lvText);
    }
    cy += 20;

    // Size tag
    const sizeText = new Text({
      text: `${def.footprintW}x${def.footprintH}`,
      style: { fontFamily: 'Offside', fontSize: 10, fill: 0x556677 },
    });
    sizeText.position.set(textX, cy);
    this.catalogContent.addChild(sizeText);
    cy += 16;

    // Description (already created above for measurement)
    descText.position.set(textX, cy);
    this.catalogContent.addChild(descText);
    cy += descH + 6;

    if (isBuilt) {
      // Current effect
      const effectLine = new Text({
        text: def.effectDescriptions[builtLevel - 1] ?? '',
        style: { fontFamily: 'Offside', fontSize: 11, fill: 0x66aa88 },
      });
      effectLine.position.set(textX, cy);
      this.catalogContent.addChild(effectLine);

      // UPGRADE inline button — right-aligned on the same row as effect text
      if (builtLevel < def.maxLevel) {
        const upgCost = def.costs[builtLevel]!;
        const canAfford = this.manager.money >= upgCost;
        const ubtn = new Container();
        const ubg = new Graphics();
        const ubw = 110;
        draw3DButton(ubg, ubw, btnH, canAfford ? 0x226633 : 0x333333, { radius: 3, depth: 2 });
        ubtn.addChild(ubg);
        const ulab = new Text({
          text: `UPG ${upgCost}Cr`,
          style: { fontFamily: 'Offside', fontSize: 12, fill: canAfford ? 0xffffff : 0x99aabb },
        });
        ulab.anchor.set(0.5);
        ulab.position.set(ubw / 2, btnH / 2);
        ubtn.addChild(ulab);
        ubtn.position.set(w - ubw - 6, cy - 4);
        ubtn.eventMode = 'static';
        ubtn.cursor = canAfford ? 'pointer' : 'not-allowed';
        if (canAfford) {
          ubtn.on('pointerover', () => { ubg.tint = 0xcccccc; });
          ubtn.on('pointerout', () => { ubg.tint = 0xffffff; });
          ubtn.on('pointerdown', () => {
            const bIdx = bs.findBuilding(def.id);
            if (bIdx >= 0) {
              const cost = bs.upgrade(bIdx, this.manager.money);
              if (cost > 0) {
                this.manager.money -= cost;
                this.manager.save();
                this.rebuildAll();
              }
            }
          });
        }
        this.catalogContent.addChild(ubtn);
      }
    } else {
      // BUILD button — right-aligned at current cursor
      const buildCost = def.costs[0]!;
      const canAfford = this.manager.money >= buildCost;
      const bbtn = new Container();
      const bbg = new Graphics();
      const bbw = 110;
      draw3DButton(bbg, bbw, btnH, canAfford ? 0x224488 : 0x333333, { radius: 3, depth: 2 });
      bbtn.addChild(bbg);
      const blab = new Text({
        text: `BUILD ${buildCost}Cr`,
        style: { fontFamily: 'Offside', fontSize: 12, fill: canAfford ? 0xffffff : 0x99aabb },
      });
      blab.anchor.set(0.5);
      blab.position.set(bbw / 2, btnH / 2);
      bbtn.addChild(blab);
      bbtn.position.set(w - bbw - 6, cy - 4);
      bbtn.eventMode = 'static';
      bbtn.cursor = canAfford ? 'pointer' : 'not-allowed';
      if (canAfford) {
        bbtn.on('pointerover', () => { bbg.tint = 0xcccccc; });
        bbtn.on('pointerout', () => { bbg.tint = 0xffffff; });
        bbtn.on('pointerdown', () => {
          this.placingBuilding = def;
          this.placingMode = 'build';
          this.movingIndex = -1;
          this.selectedBuildingIndex = -1;
          this.statusText.text = `Placing ${def.name}`;
          this.rebuildAll();
        });
      }
      this.catalogContent.addChild(bbtn);
    }

    return startY + entryH;
  }

  private exitPlacementMode() {
    this.placingBuilding = null;
    this.placingMode = 'build';
    this.movingIndex = -1;
    this.ghostGx = -1;
    this.ghostGy = -1;
    this.ghostValid = false;
    this.statusText.text = '';
  }

  private rebuildAll() {
    this.drawBuildings();
    this.buildCatalog();
    this.creditsText.text = `Cr  ${this.manager.money.toLocaleString()}`;
  }

  /** Mobile-only: toggle between the 45%-height drawer and the 40px peek strip. */
  private toggleDrawer() {
    const app = this.manager.app;
    this.panelH = BuildingState.drawerCollapsed
      ? BuildingState.DRAWER_COLLAPSED_H
      : Math.round(app.screen.height * 0.45);
    this.panelY = app.screen.height - this.panelH;
    this.mapAreaH = app.screen.height - this.panelH;

    // Resize map clip + hit area to the new map area height
    if (this.mapClip) {
      this.mapClip.clear();
      this.mapClip.rect(0, 0, this.mapAreaW, this.mapAreaH);
      this.mapClip.fill(0xffffff);
    }
    if (this.mapHitArea) {
      this.mapHitArea.clear();
      this.mapHitArea.rect(0, 0, this.mapAreaW, this.mapAreaH);
      this.mapHitArea.fill({ color: 0xffffff, alpha: 0.001 });
    }

    // Rebuild the panel (which now respects the new collapsed state + sizes)
    this.buildPanel();
  }

  exit() {
    if (this.wheelHandler) {
      this.manager.app.canvas.removeEventListener('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
    if (this.contextMenuHandler) {
      this.manager.app.canvas.removeEventListener('contextmenu', this.contextMenuHandler);
      this.contextMenuHandler = null;
    }
    this.manager.app.stage.removeChild(this.container);
    this.container.destroy({ children: true });
    this.wallSprites.clear();
    this.floorSprites.clear();
    this.wallVariantMap.clear();
  }

  update(_dt: number) {
    if (this.loading) return;

    // Update camera transform
    this.mapContainer.scale.set(this.zoom);
    const camOffX = this.mapAreaW / 2 - this.camX * TILE_SIZE * this.zoom;
    const camOffY = this.mapAreaH / 2 - this.camY * TILE_SIZE * this.zoom;
    this.mapContainer.x = camOffX;
    this.mapContainer.y = camOffY;

    // Redraw buildings (ghost footprint follows mouse)
    if (this.placingBuilding) {
      this.drawBuildings();
    }
  }
}
